// ============================================
// Light2D — 2D light components
// ============================================

// -----------------------------------------------------------------------
// Emission Mode
// -----------------------------------------------------------------------

/**
 * Emission mode determines how the light's color is specified:
 * - `'color'`       — Direct RGB color value.
 * - `'temperature'` — Color derived from Kelvin temperature + tint.
 */
export type EmissionMode = 'color' | 'temperature';

// -----------------------------------------------------------------------
// Base Interfaces
// -----------------------------------------------------------------------

/**
 * Common light properties shared by all 2D light types.
 */
export interface Light2DBase {
  /** Emission mode: direct color or temperature-derived. Default `'color'`. */
  emissionMode: EmissionMode;

  /** Color RGB (linear, 0–1). Used when `emissionMode === 'color'`. */
  color: [number, number, number];

  /** Color temperature in Kelvin (1000–40000). Used when `emissionMode === 'temperature'`. Default 6500. */
  temperature: number;

  /** Green–magenta tint offset (−1 to 1). Used with temperature mode. Default 0. */
  tint: number;

  /** Intensity multiplier (default 1.0). */
  intensity: number;

  /** Whether the light is enabled (default true). */
  enabled: boolean;
}

/**
 * Shadow properties for lights that can cast shadows.
 */
export interface ShadowProperties {
  /** Whether this light casts shadows (default false). */
  castShadows: boolean;
  /** Shadow softness — controls PCF spread (0 = hard, 1 = max soft). Default 0.3. */
  shadowSoftness: number;
  /** Shadow bias to avoid self-shadowing artifacts. Default 0.5. */
  shadowBias: number;
}

/**
 * IES (Illuminating Engineering Society) photometric profile properties.
 * Defines angular light intensity distribution for realistic luminaire shapes.
 */
export interface IESProperties {
  /** IES profile asset path. Empty string = no profile (uniform distribution). */
  iesProfile: string;
  /** How strongly the IES profile modulates the base distribution (0–1). Default 1.0. */
  iesIntensity: number;
  /**
   * Additional rotation offset for the IES pattern in degrees. Default 0.
   * - Point lights: combined with object's transform rotation.
   * - Spot lights: combined with spotlight direction angle (computed in shader).
   */
  iesRotation: number;
}

/**
 * Volumetric lighting (god rays / light shafts) properties.
 * Creates visible light scattering through fog/atmosphere.
 */
export interface VolumetricProperties {
  /** Volumetric intensity (0 = disabled, 1 = full strength). Default 0. */
  volumetricIntensity: number;
  /** Scattering density — controls how much light scatters (0.01–1.0). Default 0.1. */
  volumetricScattering: number;
}

// -----------------------------------------------------------------------
// Light Types
// -----------------------------------------------------------------------

/**
 * Point light — emits in all directions from a position.
 */
export interface PointLight2D extends Light2DBase, ShadowProperties, IESProperties, VolumetricProperties {
  readonly type: 'point';
  /** World position (x, y). */
  position: [number, number];
  /** Maximum radius of influence (world units). */
  radius: number;
  /** Falloff exponent (1 = linear, 2 = quadratic). Default 2.0. */
  falloff: number;
}

/**
 * Spot light — emits in a cone from a position in a direction.
 */
export interface SpotLight2D extends Light2DBase, ShadowProperties, IESProperties, VolumetricProperties {
  readonly type: 'spot';
  /** World position (x, y). */
  position: [number, number];
  /** Direction vector (x, y). Will be normalized on GPU. */
  direction: [number, number];
  /** Maximum radius of influence (world units). */
  radius: number;
  /** Falloff exponent. Default 2.0. */
  falloff: number;
  /** Inner cone angle in radians (full intensity). */
  innerAngle: number;
  /** Outer cone angle in radians (attenuation edge). */
  outerAngle: number;
}

/**
 * Directional light — infinite parallel rays from a direction (e.g. sunlight).
 */
export interface DirectionalLight2D extends Light2DBase {
  readonly type: 'directional';
  /** Light direction (x, y). Will be normalized on GPU. */
  direction: [number, number];
}

/**
 * Ambient light — constant illumination everywhere.
 */
export interface AmbientLight2D {
  readonly type: 'ambient';
  /** Emission mode: direct color or temperature-derived. Default `'color'`. */
  emissionMode: EmissionMode;
  /** Color RGB (linear, 0–1). Used when `emissionMode === 'color'`. */
  color: [number, number, number];
  /** Color temperature in Kelvin (1000–40000). Default 6500. */
  temperature: number;
  /** Green–magenta tint offset (−1 to 1). Default 0. */
  tint: number;
  /** Intensity multiplier. */
  intensity: number;
}

/** Union of all light types. */
export type Light2D = PointLight2D | SpotLight2D | DirectionalLight2D | AmbientLight2D;

// -----------------------------------------------------------------------
// Factory helpers
// -----------------------------------------------------------------------

/** Default light color — white. */
const DEFAULT_COLOR: [number, number, number] = [1, 1, 1];

/** Default IES properties. */
const DEFAULT_IES: IESProperties = {
  iesProfile: '',
  iesIntensity: 1.0,
  iesRotation: 0,
};

/** Default volumetric properties (disabled by default). */
const DEFAULT_VOLUMETRIC: VolumetricProperties = {
  volumetricIntensity: 0,
  volumetricScattering: 0.1,
};

/** Create a point light. */
export function createPointLight(opts: Partial<Omit<PointLight2D, 'type'>> = {}): PointLight2D {
  return {
    type: 'point',
    emissionMode: opts.emissionMode ?? 'color',
    position: opts.position ?? [0, 0],
    color: opts.color ?? [...DEFAULT_COLOR],
    temperature: opts.temperature ?? 6500,
    tint: opts.tint ?? 0,
    intensity: opts.intensity ?? 1.0,
    radius: opts.radius ?? 256,
    falloff: opts.falloff ?? 2.0,
    enabled: opts.enabled ?? true,
    castShadows: opts.castShadows ?? false,
    shadowSoftness: opts.shadowSoftness ?? 0.3,
    shadowBias: opts.shadowBias ?? 0.01,
    iesProfile: opts.iesProfile ?? DEFAULT_IES.iesProfile,
    iesIntensity: opts.iesIntensity ?? DEFAULT_IES.iesIntensity,
    iesRotation: opts.iesRotation ?? DEFAULT_IES.iesRotation,
    volumetricIntensity: opts.volumetricIntensity ?? DEFAULT_VOLUMETRIC.volumetricIntensity,
    volumetricScattering: opts.volumetricScattering ?? DEFAULT_VOLUMETRIC.volumetricScattering,
  };
}

/** Create a spot light. */
export function createSpotLight(opts: Partial<Omit<SpotLight2D, 'type'>> = {}): SpotLight2D {
  return {
    type: 'spot',
    emissionMode: opts.emissionMode ?? 'color',
    position: opts.position ?? [0, 0],
    direction: opts.direction ?? [0, -1],
    color: opts.color ?? [...DEFAULT_COLOR],
    temperature: opts.temperature ?? 6500,
    tint: opts.tint ?? 0,
    intensity: opts.intensity ?? 1.0,
    radius: opts.radius ?? 256,
    falloff: opts.falloff ?? 2.0,
    innerAngle: opts.innerAngle ?? Math.PI / 6,
    outerAngle: opts.outerAngle ?? Math.PI / 4,
    enabled: opts.enabled ?? true,
    castShadows: opts.castShadows ?? false,
    shadowSoftness: opts.shadowSoftness ?? 0.3,
    shadowBias: opts.shadowBias ?? 0.01,
    iesProfile: opts.iesProfile ?? DEFAULT_IES.iesProfile,
    iesIntensity: opts.iesIntensity ?? DEFAULT_IES.iesIntensity,
    iesRotation: opts.iesRotation ?? DEFAULT_IES.iesRotation,
    volumetricIntensity: opts.volumetricIntensity ?? DEFAULT_VOLUMETRIC.volumetricIntensity,
    volumetricScattering: opts.volumetricScattering ?? DEFAULT_VOLUMETRIC.volumetricScattering,
  };
}

/** Create a directional light. */
export function createDirectionalLight(
  opts: Partial<Omit<DirectionalLight2D, 'type'>> = {},
): DirectionalLight2D {
  return {
    type: 'directional',
    emissionMode: opts.emissionMode ?? 'color',
    direction: opts.direction ?? [0, -1],
    color: opts.color ?? [...DEFAULT_COLOR],
    temperature: opts.temperature ?? 6500,
    tint: opts.tint ?? 0,
    intensity: opts.intensity ?? 1.0,
    enabled: opts.enabled ?? true,
  };
}

/** Create an ambient light. */
export function createAmbientLight(
  opts: Partial<Omit<AmbientLight2D, 'type'>> = {},
): AmbientLight2D {
  return {
    type: 'ambient',
    emissionMode: opts.emissionMode ?? 'color',
    color: opts.color ?? [0.1, 0.1, 0.1],
    temperature: opts.temperature ?? 6500,
    tint: opts.tint ?? 0,
    intensity: opts.intensity ?? 1.0,
  };
}
